import { SignatureTemplateStatus } from '@commenda/prisma';
import { SWRFetcher, fetcher } from '@commenda/utils';
import useSWR from 'swr';

export interface SignatureTemplateRole {
  id: string;
  externalRecipientId: string;
  roleName: string;
  displayName: string;
  /** Authored in the provider's embedded edit view; display-only in Commenda. */
  routingOrder: number;
  signatureTemplateId: string;
}

export interface SignatureTemplate {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  description: string | null;
  externalTemplateId: string;
  status: SignatureTemplateStatus;
  sourceFileId: number | null;
  createdByUserId: string | null;
  Roles: SignatureTemplateRole[];
  SourceFile: { id: number; fileName: string; fileUrl: string } | null;
}

/** A workflow template version whose definition references a signing template. */
export interface TemplateWorkflowReference {
  versionId: string;
  version: string;
  status: string;
  workflowTemplateId: string;
  workflowTemplateName: string;
}

export type SignatureTemplateDetail = SignatureTemplate & {
  referencedByWorkflows: TemplateWorkflowReference[];
};

export interface SignatureTemplateRoleInput {
  /** Present when updating an existing role; omitted for new roles. */
  id?: string;
  roleName: string;
  displayName?: string;
}

export interface CreateSignatureTemplateRequest {
  name: string;
  description?: string;
  /** Reference an existing File as the template document... */
  sourceFileId?: number;
  /** ...or a freshly uploaded one. Exactly one of sourceFileId / file is required. */
  file?: { fileName: string; fileUrl: string };
  roles: SignatureTemplateRoleInput[];
}

export interface UpdateSignatureTemplateRequest {
  name?: string;
  description?: string;
  /** Full desired role list; structural changes are rejected while a PUBLISHED workflow version references the template. */
  roles?: SignatureTemplateRoleInput[];
}

export const useSignatureTemplates = (status?: SignatureTemplateStatus) => {
  const url = `e-signature/templates${status ? `?status=${status}` : ''}`;

  const { data, error, mutate } = useSWR<SignatureTemplate[]>(
    url,
    SWRFetcher({ method: 'GET' })
  );

  return {
    templates: data,
    error,
    mutate,
    isLoading: !data && !error,
  };
};

export const useSignatureTemplate = (templateId: string | null) => {
  const { data, error, mutate } = useSWR<SignatureTemplateDetail>(
    templateId ? `e-signature/templates/${templateId}` : null,
    SWRFetcher({ method: 'GET' })
  );

  return {
    template: data,
    error,
    mutate,
    isLoading: templateId != null && !data && !error,
  };
};

export async function createSignatureTemplate(
  data: CreateSignatureTemplateRequest
): Promise<Response> {
  return fetcher('e-signature/templates', {
    method: 'POST',
    body: data,
  });
}

export async function updateSignatureTemplate(
  templateId: string,
  data: UpdateSignatureTemplateRequest
): Promise<Response> {
  return fetcher(`e-signature/templates/${templateId}`, {
    method: 'PATCH',
    body: data,
  });
}

/** Embedded DocuSign template-edit view (field placement + signing order). */
export async function getTemplateEditView(
  templateId: string,
  returnUrl: string
): Promise<Response> {
  return fetcher(`e-signature/templates/${templateId}/edit-view`, {
    method: 'POST',
    body: { returnUrl },
  });
}

/** Call when an embedded edit session ends: pulls roles + routing order back from the provider. */
export async function syncSignatureTemplate(
  templateId: string
): Promise<Response> {
  return fetcher(`e-signature/templates/${templateId}/sync`, {
    method: 'POST',
    body: {},
  });
}

export async function publishSignatureTemplate(
  templateId: string
): Promise<Response> {
  return fetcher(`e-signature/templates/${templateId}/publish`, {
    method: 'POST',
    body: {},
  });
}

export async function archiveSignatureTemplate(
  templateId: string
): Promise<Response> {
  return fetcher(`e-signature/templates/${templateId}/archive`, {
    method: 'POST',
    body: {},
  });
}
